import React, { useEffect, useRef } from "react";
import styled from "styled-components";

import { StarterTemplatesPaneControllerState } from "js/controllers/StarterTemplatesPaneController";
import { formatStarterTemplateCategoryLabel } from "js/editor/CreatePresentation/starterTemplates/starterTemplateUtils";
import { FlexSpacer } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { useIntersectionObserver } from "js/react/hooks/useIntersectionObserver";
import { themeColors } from "js/react/sharedStyles";
import { EmptySlideThumbnail, SlideThumbnail } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import {
    PresentationTemplateCard,
    PresentationTemplateHeader,
    PresentationTemplateSlideRail,
    PresentationTemplateSlideRailContainer,
} from "js/react/views/SharedResources/Components/PresentationTemplateCardStyles";

const CategoryTag = styled.span`
    padding: 3px 5px;
    border-radius: 3px;
    font-size: 12px;
    font-weight: 600;
    color: #888;
    line-height: 125%;
    background: #F4F4F4;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
        color: ${themeColors.ui_blue};
        background: ${themeColors.lightBlue};
    }
`;

const LazySlideThumbnailContainer = styled.div`
    min-width: 0;
`;

interface LazySlideThumbnailProps {
    slideId: string;
    presentationOrLinkId: string;
    modifiedAt?: number;
    readOnly?: boolean;
}

function LazySlideThumbnail({
    slideId,
    presentationOrLinkId,
    modifiedAt,
    readOnly,
}: LazySlideThumbnailProps) {
    const thumbnailRef = useRef<HTMLDivElement>(null);
    const isVisible = useIntersectionObserver(thumbnailRef);

    return (
        <LazySlideThumbnailContainer ref={thumbnailRef}>
            {isVisible ? (
                <SlideThumbnail
                    slideId={slideId}
                    presentationOrLinkId={presentationOrLinkId}
                    modifiedAt={modifiedAt}
                    readOnly={readOnly}
                />
            ) : (
                <EmptySlideThumbnail />
            )}
        </LazySlideThumbnailContainer>
    );
}

export interface StarterTemplateItemProps {
    record: StarterTemplatesPaneControllerState["data"][number];
    selected: boolean;
    onSelect: (presentationId: string, isDoubleClick?: boolean) => void;
    onCategoryClick?: (category: string) => void;
    onVisibleChange?: (isVisible: boolean) => void;
}

export function StarterTemplateItem({
    record,
    selected,
    onSelect,
    onCategoryClick,
    onVisibleChange
}: StarterTemplateItemProps) {
    const containerRef = useRef<HTMLDivElement>(null);

    const isIntersecting = useIntersectionObserver(containerRef, {
        rootMargin: "200px 0px",
    });

    useEffect(() => {
        onVisibleChange?.(isIntersecting);
    }, [isIntersecting, onVisibleChange]);

    const { document, resource } = record;

    const presentationId = document.id;
    const name = document.name;
    const tags = resource.tags;
    const slideIds = Object.entries(document.slideRefs).sort(([, a], [, b]) => a - b).map(([slideId]) => slideId);

    return (
        <PresentationTemplateCard
            ref={containerRef}
            data-item-id={presentationId}
            className={selected ? "selected" : ""}
            onClick={() => onSelect(presentationId, false)}
            onDoubleClick={() => onSelect(presentationId, true)}
        >
            <PresentationTemplateHeader>
                <div className="title">{name}</div>
                {tags.length > 0 && (
                    <>
                        <FlexSpacer />
                        <FlexBox center gap={5}>
                            {tags.map(tag => (
                                <CategoryTag
                                    key={tag}
                                    data-test-id={`starter-template-category-${tag}`}
                                    onClick={event => {
                                        event.stopPropagation();
                                        onCategoryClick?.(tag);
                                    }}
                                    onDoubleClick={event => event.stopPropagation()}
                                >
                                    {formatStarterTemplateCategoryLabel(tag)}
                                </CategoryTag>
                            ))}
                        </FlexBox>
                    </>
                )}
            </PresentationTemplateHeader>
            <PresentationTemplateSlideRailContainer>
                <PresentationTemplateSlideRail>
                    {isIntersecting && slideIds.map(slideId => (
                        <LazySlideThumbnail
                            key={slideId}
                            slideId={slideId}
                            presentationOrLinkId={presentationId}
                            // Use document.modifiedAt here because presentation templates are static, and seed data change will
                            // update the modifiedAt on the resource
                            modifiedAt={resource.modifiedAt}
                            readOnly
                        />
                    ))}
                    {!isIntersecting && <EmptySlideThumbnail />}
                </PresentationTemplateSlideRail>
            </PresentationTemplateSlideRailContainer>
        </PresentationTemplateCard>
    );
}
